import { useState } from "react";

import { API_BASE_URL, getApiHeaders, getApiHeadersMultipart } from "../../../../config";

/**
 * Image state and the three image endpoints of a listing.
 *
 * Verbatim move out of AdminListingsPage.js (L1 extraction). Discovery
 * risk R3 is deliberately untouched: no set-primary, no reorder call, no
 * change to how `is_main` is derived on upload, and upload failures are
 * still swallowed exactly as before.
 *
 * `editingListingId` is owned by the page, because the delete and edit
 * upload handlers need it while the form hook needs these setters — the
 * page holds that one piece of state so the two hooks stay acyclic.
 * `onError` is the form's createError setter; a failed delete still shows
 * its message in the same place as before.
 */
export default function useListingImages({ editingListingId, onError }) {
  const [createdListingId, setCreatedListingId] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [editUploading, setEditUploading] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);

  const handleImageUpload = async (files) => {
    if (!createdListingId || !files || files.length === 0) return;
    setUploadingImages(true);
    for (let i = 0; i < files.length; i++) {
      const isMain = uploadedImages.length === 0 && i === 0;
      const formData = new FormData();
      formData.append("file", files[i]);
      formData.append("is_main", isMain ? "true" : "false");
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/admin/listings/${createdListingId}/images/upload`,
          { method: "POST", headers: getApiHeadersMultipart(), body: formData }
        );
        if (res.ok) {
          const img = await res.json();
          setUploadedImages((prev) => [...prev, img]);
        }
      } catch (err) {
        console.error("Upload failed:", err);
      }
    }
    setUploadingImages(false);
  };

  const handleDeleteExistingImage = async (imageId) => {
    if (!editingListingId || !imageId) return;
    setDeletingImageId(imageId);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/listings/${encodeURIComponent(editingListingId)}/images/${encodeURIComponent(imageId)}`,
        { method: "DELETE", headers: getApiHeaders() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Löschen fehlgeschlagen.");
      }
      setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      onError(err.message || "Löschen fehlgeschlagen.");
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleEditImageUpload = async (files) => {
    if (!editingListingId || !files || files.length === 0) return;
    setEditUploading(true);
    for (let i = 0; i < files.length; i++) {
      const isMain = existingImages.length === 0 && i === 0;
      const formData = new FormData();
      formData.append("file", files[i]);
      formData.append("is_main", isMain ? "true" : "false");
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/admin/listings/${encodeURIComponent(editingListingId)}/images/upload`,
          { method: "POST", headers: getApiHeadersMultipart(), body: formData }
        );
        if (res.ok) {
          const img = await res.json();
          setExistingImages((prev) => [...prev, img]);
        }
      } catch (err) {
        console.error("Upload failed:", err);
      }
    }
    setEditUploading(false);
  };

  return {
    createdListingId,
    setCreatedListingId,
    uploadingImages,
    uploadedImages,
    setUploadedImages,
    existingImages,
    setExistingImages,
    editUploading,
    deletingImageId,
    handleImageUpload,
    handleDeleteExistingImage,
    handleEditImageUpload,
  };
}
